import { SelectMenu, Typography } from '@scale/scaleui';
import {
  DEFAULT_INSTRUCTION_TITLE,
  DEFAULT_INSTRUCTION_SUBTITLE,
  InteractionStepType,
  ChatMessageDisplayType,
  AttachmentMetadataType,
} from '@scale/shared/types/chat';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import {
  ConditionalStepConfig,
  EnableRTLTextConfig,
  ReviewLevelConfig,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import { StepConfigurationComponentProps } from '../stepDescriptor/types';
import { emailSchema } from 'react-scale/components/scalers/tasks/textcollection/WorkspaceComponents/EmailView';
import { useCallback, useState, useEffect } from 'react';
import { populateTemplateHelper } from '@scale/shared/taskTemplate/helpers';
import { Icon } from '@scale/scaleui-radix';

const ATTACHMENT_VIEWER_OPTIONS = [
  { name: 'None', id: 'None' },
  { name: 'Email', id: AttachmentMetadataType.Email },
];

export function InstructionConfigurationComponent({
  params: currentParams,
  setParams: setCurrentParams,
  previousParams,
  taskParams,
}: StepConfigurationComponentProps<InteractionStepType.Instruction>) {
  const [areEmailAttachmentsValid, setAreEmailAttachmentsValid] = useState<boolean | null>(null);
  const [emailAttachmentValidationErrors, setEmailAttachmentValidationErrors] = useState<string[]>(
    [],
  );

  /**
   * Validate the email attachments JSON against the expected Email Attachment schema.
   * This is used to ensure operators know whether their JSON is valid.
   */
  const validateEmailAttachmentsJSON = useCallback(async () => {
    try {
      const populatedCurrentParams = await populateTemplateHelper(
        currentParams.params,
        taskParams?.templateVariables ?? {},
        { parseJson: false },
      );

      const emailEntries = JSON.parse(
        populatedCurrentParams.attachment_viewer_config?.email_attachments_json,
      ) as any[];

      // Reset errors before validation
      const errors: string[] = [];

      // Validate each entry. If any entry is invalid, collect errors.
      for (const entry of emailEntries) {
        const parsedEntry = emailSchema.safeParse(entry);
        if (!parsedEntry.success) {
          // Collect error messages
          errors.push(parsedEntry.error.message || 'Email schema validation failed');
        }
      }

      if (errors.length > 0) {
        setEmailAttachmentValidationErrors(errors);
        setAreEmailAttachmentsValid(false);
        return;
      }

      setEmailAttachmentValidationErrors([]);
      setAreEmailAttachmentsValid(true);
      return;
    } catch (e) {
      setEmailAttachmentValidationErrors([(e as Error).message || 'Invalid JSON format']);
      setAreEmailAttachmentsValid(false);
    }
  }, [currentParams.params, taskParams?.templateVariables]);

  useEffect(() => {
    if (
      currentParams.params.attachment_viewer_type === AttachmentMetadataType.Email &&
      currentParams.params.attachment_viewer_config?.email_attachments_json
    ) {
      validateEmailAttachmentsJSON();
    }
  }, [
    currentParams.params.attachment_viewer_type,
    currentParams.params.attachment_viewer_config?.email_attachments_json,
    validateEmailAttachmentsJSON,
  ]);

  return (
    <div className="flex flex-col gap-4">
      <StringInputBuilder
        name={'Title'}
        placeholder={DEFAULT_INSTRUCTION_TITLE}
        existingValue={currentParams.title}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, title: value })}
        required={true}
      />

      <StringInputBuilder
        name={'Subtitle'}
        placeholder={DEFAULT_INSTRUCTION_SUBTITLE}
        existingValue={currentParams.subtitle}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, subtitle: value })}
      />

      <SelectMenu
        label={'Display Type'}
        options={[
          { name: 'Markdown', id: ChatMessageDisplayType.Markdown },
          { name: 'Plaintext', id: ChatMessageDisplayType.Plaintext },
        ]}
        selectedOptionId={currentParams.params.display_type ?? ChatMessageDisplayType.Markdown}
        setSelected={(id: string) =>
          setCurrentParams({
            ...currentParams,
            params: {
              ...currentParams.params,
              display_type: id,
            },
          })
        }
      />

      <StringInputBuilder
        name={'Instructions'}
        hint={'Instructions for the user to follow.'}
        existingValue={currentParams.params.instructions}
        updateProperty={(value: string) =>
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, instructions: value },
          })
        }
        useMarkdownEditor={
          (currentParams.params.display_type ?? ChatMessageDisplayType.Markdown) ===
          ChatMessageDisplayType.Markdown
        }
      />

      <SelectMenu
        label={'Select Attachment Viewer'}
        options={ATTACHMENT_VIEWER_OPTIONS}
        selectedOptionId={currentParams.params.attachment_viewer_type ?? 'None'}
        setSelected={(id: string) =>
          setCurrentParams({
            ...currentParams,
            params: {
              ...currentParams.params,
              attachment_viewer_type: id,
            },
          })
        }
      />

      {currentParams.params.attachment_viewer_type === AttachmentMetadataType.Email && (
        <div className="flex flex-col gap-2">
          <StringInputBuilder
            name={'Email Attachments JSON'}
            hint={
              'JSON for the email attachment viewer. Click outside the text box to validate the JSON against the expected Email Attachment schema.'
            }
            existingValue={
              currentParams.params.attachment_viewer_config?.email_attachments_json ?? ''
            }
            updateProperty={(value: string) => {
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  attachment_viewer_config: {
                    ...currentParams.params.attachment_viewer_config,
                    email_attachments_json: value,
                  },
                },
              });
            }}
            useMarkdownEditor={false}
          />

          <Typography.Paragraph3 className="text-primary-500">
            <a
              href="https://docs.google.com/document/d/1v8oIcE1ZDxa-Atvg0iUuB0IS8FEgPwiJl1t2xFgVJd4/edit?tab=t.0"
              target="_blank"
              rel="noopener noreferrer"
            >
              See Expected Email Attachment Schema
            </a>
          </Typography.Paragraph3>

          {areEmailAttachmentsValid === false && (
            <div className="flex flex-col gap-1">
              <div className="flex justify-center items-center gap-2">
                <Icon
                  icon={{ prefix: 'fas', iconName: 'exclamation-triangle' }}
                  className="text-danger-500"
                  label="Invalid Email Attachments JSON"
                />
                <Typography.Paragraph3 className="text-danger-500">
                  Invalid Email Attachments JSON
                </Typography.Paragraph3>
              </div>
              {emailAttachmentValidationErrors.length > 0 && (
                <div className="flex flex-col gap-1 justify-center items-center">
                  {emailAttachmentValidationErrors.map((error, index) => (
                    <Typography.Paragraph3 className="text-danger-500" key={index}>
                      {error}
                    </Typography.Paragraph3>
                  ))}
                </div>
              )}
            </div>
          )}
          {areEmailAttachmentsValid === true && (
            <div className="flex justify-center items-center gap-2 pt-1">
              <Icon
                icon={{ prefix: 'fas', iconName: 'check-circle' }}
                className="text-success-500"
                label="Valid Email Attachments JSON"
              />
              <Typography.Paragraph3 className="text-success-500">
                Valid Email Attachments JSON
              </Typography.Paragraph3>
            </div>
          )}
        </div>
      )}

      <EnableRTLTextConfig
        value={currentParams.params.enable_rtl_text}
        onChange={value => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, enable_rtl_text: value },
          });
        }}
      />

      <ReviewLevelConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />
      <ConditionalStepConfig
        currentParams={currentParams}
        setCurrentParams={setCurrentParams}
        previousParams={previousParams}
        taskParams={taskParams}
      />
    </div>
  );
}
